/**
 * CameraViewsDialog — designer picks up to 4 camera angles to photograph
 * the current piece from. Replaces the legacy Multi-view (L/B/R only).
 *
 * Flow:
 *   1. Pick 1–4 angles (mix of presets + Custom slider angles).
 *   2. Submit → backend kicks off Nano Banana, returns a job_id.
 *   3. Parent polls /api/studio/multiview-job/{job_id} via the existing
 *      pollMultiviewJob helper and merges new entries into the gallery.
 */
import { useEffect, useMemo, useState } from "react";
import { Loader2, Camera, Plus, X, Sliders } from "lucide-react";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

// Preset chips. The icons / SVG glyphs each describe the camera position
// visually so designers can spot the angle without reading.
const PRESETS = [
  {
    id: "topdown",
    label: "Top-down",
    hint: "Bird's-eye flat-lay",
    glyph: (
      <svg width="34" height="22" viewBox="0 0 34 22" fill="none">
        <ellipse cx="17" cy="14" rx="9" ry="4" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="17" cy="4" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M17 6.5 L17 9.5" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.5 1.5" />
      </svg>
    ),
  },
  {
    id: "34front",
    label: "3/4 Front",
    hint: "Classic catalog angle",
    glyph: (
      <svg width="34" height="22" viewBox="0 0 34 22" fill="none">
        <ellipse cx="17" cy="14" rx="9" ry="4" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="26" cy="6" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M24 7.5 L18.5 12.5" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.5 1.5" />
      </svg>
    ),
  },
  {
    id: "profile",
    label: "Side view",
    hint: "Pure 90° side",
    glyph: (
      <svg width="34" height="22" viewBox="0 0 34 22" fill="none">
        <ellipse cx="17" cy="14" rx="9" ry="4" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="30" cy="14" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M28 14 L26 14" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.5 1.5" />
      </svg>
    ),
  },
  {
    id: "tilt45",
    label: "45° Tilt",
    hint: "Elevated three-quarter",
    glyph: (
      <svg width="34" height="22" viewBox="0 0 34 22" fill="none">
        <ellipse cx="17" cy="14" rx="9" ry="4" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="29" cy="3" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M27 5 L19 12" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.5 1.5" />
      </svg>
    ),
  },
  {
    id: "hero",
    label: "Hero",
    hint: "Dramatic low angle",
    glyph: (
      <svg width="34" height="22" viewBox="0 0 34 22" fill="none">
        <ellipse cx="17" cy="14" rx="9" ry="4" stroke="currentColor" strokeWidth="1.4" />
        <circle cx="29" cy="20" r="2.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M27 19 L20 16" stroke="currentColor" strokeWidth="1.2" strokeDasharray="1.5 1.5" />
      </svg>
    ),
  },
];

const MAX_ANGLES = 4;
const COST_CREDITS = 3;

// Stable client-side view-id (must match backend `_angle_to_view_id`).
const viewIdOf = (a) =>
  a.preset === "custom"
    ? `cam_custom_${Math.round(a.azimuth)}_${Math.round(a.elevation)}`
    : `cam_${a.preset}`;

export default function CameraViewsDialog({
  open,
  onOpenChange,
  sourceId,        // gen_id to photograph (front / refined front / side view)
  onSubmit,        // async (angles) => job_id  (parent owns the polling)
  creditsRemaining,
}) {
  const [selected, setSelected] = useState([]); // array of { preset, azimuth?, elevation? }
  const [customOpen, setCustomOpen] = useState(false);
  const [azimuth, setAzimuth] = useState(45);
  const [elevation, setElevation] = useState(15);
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setSelected([]);
      setCustomOpen(false);
      setAzimuth(45);
      setElevation(15);
      setSubmitting(false);
    }
  }, [open]);

  const togglePreset = (presetId) => {
    setSelected((s) => {
      const key = `cam_${presetId}`;
      const idx = s.findIndex((a) => viewIdOf(a) === key);
      if (idx >= 0) return s.filter((_, i) => i !== idx);
      if (s.length >= MAX_ANGLES) {
        toast.error(`You can pick up to ${MAX_ANGLES} angles per job.`);
        return s;
      }
      return [...s, { preset: presetId }];
    });
  };

  const addCustom = () => {
    setSelected((s) => {
      if (s.length >= MAX_ANGLES) {
        toast.error(`You can pick up to ${MAX_ANGLES} angles per job.`);
        return s;
      }
      const candidate = { preset: "custom", azimuth, elevation };
      const key = viewIdOf(candidate);
      if (s.some((a) => viewIdOf(a) === key)) {
        toast.error("That custom angle is already in the list.");
        return s;
      }
      return [...s, candidate];
    });
  };

  const removeAt = (i) => setSelected((s) => s.filter((_, idx) => idx !== i));

  const presetActive = (presetId) =>
    selected.some((a) => viewIdOf(a) === `cam_${presetId}`);

  const canSubmit = useMemo(
    () => selected.length >= 1 && !submitting && creditsRemaining >= COST_CREDITS,
    [selected, submitting, creditsRemaining],
  );

  const submit = async () => {
    if (!sourceId || !canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/studio/camera-views/${sourceId}`,
        { angles: selected },
        { timeout: 30000 },
      );
      if (!data?.job_id) throw new Error("No job id returned");
      // Hand off to the parent — it owns the existing polling pipeline.
      await onSubmit?.(data.job_id, selected);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to start camera views."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="camera-views-dialog"
        className="max-w-2xl bg-[#0B0B0B] border-white/10 text-white"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Camera className="w-4 h-4 text-[#D4AF37]" /> Camera views
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Pick up to {MAX_ANGLES} angles. Flat {COST_CREDITS} credits per job —
            we keep the piece&apos;s exact geometry, only the camera moves.
          </DialogDescription>
        </DialogHeader>

        {/* Preset chips */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
          {PRESETS.map((p) => {
            const active = presetActive(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => togglePreset(p.id)}
                data-testid={`cam-preset-${p.id}`}
                className={`rounded-xl border px-3 py-3 flex flex-col items-center gap-1.5 transition ${
                  active
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
                    : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/25 hover:text-white"
                }`}
              >
                <div className={active ? "text-[#D4AF37]" : "text-white/55"}>{p.glyph}</div>
                <div className="text-[11px] font-medium leading-tight">{p.label}</div>
                <div className="text-[9px] uppercase tracking-[0.14em] text-white/35 leading-tight">{p.hint}</div>
              </button>
            );
          })}
        </div>

        {/* Custom panel */}
        <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <button
            type="button"
            onClick={() => setCustomOpen((v) => !v)}
            data-testid="cam-custom-toggle"
            className="w-full flex items-center justify-between px-3 py-2 text-[12px] text-white/75 hover:text-white"
          >
            <span className="flex items-center gap-1.5">
              <Sliders className="w-3.5 h-3.5" /> Custom angle
            </span>
            <span className="text-[10px] text-white/40">
              {customOpen ? "Hide" : "Adjust azimuth + elevation"}
            </span>
          </button>
          {customOpen && (
            <div className="px-3 pb-3 space-y-3 border-t border-white/10 pt-3">
              <div>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">
                  <span>Azimuth (rotation)</span>
                  <span className="text-[#D4AF37]">{azimuth}°</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={359}
                  step={5}
                  value={azimuth}
                  onChange={(e) => setAzimuth(Number(e.target.value))}
                  data-testid="cam-custom-azimuth"
                  className="w-full accent-[#D4AF37]"
                />
                <div className="flex justify-between text-[9px] text-white/30 mt-1">
                  <span>0° front</span><span>90° right</span><span>180° back</span><span>270° left</span><span>360°</span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">
                  <span>Elevation (tilt)</span>
                  <span className="text-[#D4AF37]">{elevation}°</span>
                </div>
                <input
                  type="range"
                  min={-30}
                  max={75}
                  step={5}
                  value={elevation}
                  onChange={(e) => setElevation(Number(e.target.value))}
                  data-testid="cam-custom-elevation"
                  className="w-full accent-[#D4AF37]"
                />
                <div className="flex justify-between text-[9px] text-white/30 mt-1">
                  <span>-30° hero</span><span>0° eye-level</span><span>45°</span><span>75° top</span>
                </div>
              </div>
              <button
                type="button"
                onClick={addCustom}
                data-testid="cam-custom-add"
                className="w-full text-[11px] inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition"
              >
                <Plus className="w-3.5 h-3.5" /> Add this angle
              </button>
            </div>
          )}
        </div>

        {/* Selected list */}
        {selected.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1.5">
              Picked angles ({selected.length}/{MAX_ANGLES})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {selected.map((a, i) => {
                const label =
                  a.preset === "custom"
                    ? `Custom ${a.azimuth}° / ${a.elevation}°`
                    : PRESETS.find((p) => p.id === a.preset)?.label || a.preset;
                return (
                  <span
                    key={`${viewIdOf(a)}_${i}`}
                    data-testid={`cam-selected-${viewIdOf(a)}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#D4AF37]/12 border border-[#D4AF37]/35 text-[#D4AF37] text-[11px]"
                  >
                    {label}
                    <button
                      type="button"
                      onClick={() => removeAt(i)}
                      className="opacity-70 hover:opacity-100"
                      aria-label="Remove angle"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <div className="text-[11px] text-white/45">
            {COST_CREDITS} credits flat · {creditsRemaining} renders left
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="text-[12px] px-3 py-1.5 rounded-md border border-white/10 text-white/65 hover:text-white hover:border-white/25 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              data-testid="cam-submit-btn"
              className="text-[12px] inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md border border-[#D4AF37]/55 bg-[#D4AF37] text-black font-medium hover:bg-[#D4AF37]/90 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Starting…</>
              ) : (
                <><Camera className="w-3.5 h-3.5" /> Generate {selected.length || ""} {selected.length === 1 ? "angle" : "angles"} · {COST_CREDITS} cr</>
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
